/**
 * Shared `ShippingMethod` shape returned to clients by every endpoint that
 * surfaces shipping options for a listing — `/api/listing-meta` and
 * `/api/cart` both return arrays of this exact shape (and additionally
 * `/api/cart` carries it as `selectedShippingMethod`).
 *
 * Why this is a separate file:
 *   - Two routes serialise the same row; keeping them schema-aligned by file
 *     beats two parallel declarations that quietly drift.
 *   - Mobile already vendored a near-identical type (`CartShippingMethod` in
 *     `apps/mobile/lib/api-kraahl.ts:201-214`). The vendored mobile shape is
 *     dual-name tolerant (`extra_value`/`extraValue`, `allow_combined`/
 *     `allowCombinedShipping`) because of past server inconsistency; this
 *     contract pins ONLY the camelCase form the routes actually emit today.
 *     The mobile fallback parser stays valid (it just won't trigger).
 *
 * Field semantics:
 *   - `price` is what the seller charges to ship the FIRST item using this
 *     method.
 *   - `extraValue` is what the seller charges for each ADDITIONAL item in the
 *     same order when `allowCombinedShipping` is true (multi-quantity / cart
 *     of same listing); otherwise it's irrelevant.
 *   - `bannedCountries` is the ISO-3166 alpha-2 deny-list — the buyer's
 *     destination is checked against this client-side AND server-side.
 *
 * @see app/api/listing-meta/route.ts — emits as `shippingMethods[]`
 * @see app/api/cart/route.ts — emits as `listings.shippingMethods[]` + `selectedShippingMethod`
 * @see apps/mobile/lib/api-kraahl.ts CartShippingMethod — sibling vendored type
 */

import { z } from "zod";
import { pinned } from "./pin";

export interface ShippingMethod {
  id: string;
  name: string;
  description: string;
  /** Base shipping cost for the first item. Currency-less number (route's currency). */
  price: number;
  /** Per-additional-item surcharge when combined shipping is allowed. */
  extraValue: number;
  /** When true, multi-quantity orders pay `price + (qty-1) * extraValue`. */
  allowCombinedShipping: boolean;
  /** ISO-3166 alpha-2 list (e.g. ["US", "CA"]) — ship-to is rejected when matched. */
  bannedCountries: string[];
}

export const ShippingMethodSchema = pinned<ShippingMethod>()(
  z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    price: z.number(),
    extraValue: z.number(),
    allowCombinedShipping: z.boolean(),
    bannedCountries: z.array(z.string()),
  }),
);
